import type { ContributionDetailsFormProps } from '../../components/contributions/ContributionDetailsForm';
import type { ContributionCategoryMeta } from '../../components/contributions/ContributionDetailsForm';
import { roadLayoutLabel } from '../../utils/roadContributionFlow';
import type { StripCaptureMode } from '../../utils/roadStripSurvey';
import type { LaneDirection } from '../../components/contributions/roadFlowUtils';
import type { RoadDetailsState } from './useRoadDetailsState';

type RoadFormProps = Pick<
  ContributionDetailsFormProps,
  | 'roadSurface'
  | 'onRoadSurfaceChange'
  | 'roadSmoothness'
  | 'onRoadSmoothnessChange'
  | 'roadCarriagewayCount'
  | 'onRoadCarriagewayCountChange'
  | 'roadLanes'
  | 'onRoadLanesChange'
  | 'roadLanesForward'
  | 'onRoadLanesForwardChange'
  | 'roadLanesBackward'
  | 'onRoadLanesBackwardChange'
  | 'roadLaneDirections'
  | 'onRoadLaneDirectionsChange'
  | 'roadManualLaneDirections'
  | 'onRoadManualLaneDirectionsChange'
  | 'roadUsePerLaneWidths'
  | 'onRoadUsePerLaneWidthsChange'
  | 'roadLaneWidthsM'
  | 'onRoadLaneWidthsMChange'
  | 'roadCarriagewayALaneWidthsM'
  | 'onRoadCarriagewayALaneWidthsMChange'
  | 'roadCarriagewayBLaneWidthsM'
  | 'onRoadCarriagewayBLaneWidthsMChange'
  | 'roadToll'
  | 'onRoadTollChange'
  | 'roadFloodProne'
  | 'onRoadFloodProneChange'
  | 'roadPotholes'
  | 'onRoadPotholesChange'
  | 'roadStreetVendors'
  | 'onRoadStreetVendorsChange'
  | 'roadAccidentProne'
  | 'onRoadAccidentProneChange'
  | 'roadHawkers'
  | 'onRoadHawkersChange'
  | 'roadLit'
  | 'onRoadLitChange'
  | 'roadDrainage'
  | 'onRoadDrainageChange'
  | 'roadMaxspeed'
  | 'onRoadMaxspeedChange'
  | 'roadCrossingType'
  | 'onRoadCrossingTypeChange'
  | 'roadWidth'
  | 'onRoadWidthChange'
  | 'roadCarriagewayAWidth'
  | 'onRoadCarriagewayAWidthChange'
  | 'roadCarriagewayBWidth'
  | 'onRoadCarriagewayBWidthChange'
  | 'singleStripCaptureMode'
  | 'onSingleStripCaptureModeChange'
  | 'stripACaptureMode'
  | 'onStripACaptureModeChange'
  | 'stripBCaptureMode'
  | 'onStripBCaptureModeChange'
  | 'divideWidthM'
  | 'onDivideWidthMChange'
  | 'dividedDivideReady'
  | 'dividedCarriagewayAReady'
  | 'dividedCarriagewayBReady'
  | 'dividedCarriagewayReady'
  | 'stripATraceReady'
  | 'stripBTraceReady'
  | 'singleStripTraceReady'
  | 'onStartMarkingDivide'
  | 'onStartMarkingStripA'
  | 'onStartMarkingStripB'
  | 'onStartMarkingSingleStrip'
  | 'dividedSurveySetupHint'
  | 'onDismissDividedSurveySetupHint'
  | 'roadGuidedSurveyFlow'
  | 'roadLayoutSubtitle'
  | 'roadGeometryStatusRows'
  | 'onEditRoadGeometry'
  | 'roadCarriagewayALaneDirections'
  | 'onRoadCarriagewayALaneDirectionsChange'
  | 'roadCarriagewayBLaneDirections'
  | 'onRoadCarriagewayBLaneDirectionsChange'
  | 'roadSideFacilities'
  | 'onRoadSideFacilitiesChange'
>;

export interface RoadDetailsSectionInput {
  details: RoadDetailsState;
  motorRoadSurveyFlow: boolean;
  selectedCategoryMeta: ContributionCategoryMeta | null | undefined;
  singleStripCaptureMode: StripCaptureMode;
  onSingleStripCaptureModeChange: (mode: StripCaptureMode) => void;
  stripACaptureMode: StripCaptureMode;
  onStripACaptureModeChange: (mode: StripCaptureMode) => void;
  stripBCaptureMode: StripCaptureMode;
  onStripBCaptureModeChange: (mode: StripCaptureMode) => void;
  divideWidthM: string;
  onDivideWidthMChange: (v: string) => void;
  dividedDivideReady: boolean;
  dividedCarriagewayAReady: boolean;
  dividedCarriagewayBReady: boolean;
  dividedCarriagewayReady: boolean;
  stripATraceReady: boolean;
  stripBTraceReady: boolean;
  singleStripTraceReady: boolean;
  onStartMarkingDivide: () => void;
  onStartMarkingStripA: () => void;
  onStartMarkingStripB: () => void;
  onStartMarkingSingleStrip: () => void;
  dividedSurveySetupHint: boolean;
  onDismissDividedSurveySetupHint: () => void;
  roadGeometryStatusRows: { label: string; statusLabel: string }[];
  onEditRoadGeometry?: () => void;
  onRoadCarriagewayCountChange: (count: number) => void;
}

/** Props bundle for ContributionDetailsForm road accordions + guided survey UI. */
export function buildRoadDetailsFormProps(input: RoadDetailsSectionInput): RoadFormProps {
  const { details: d, motorRoadSurveyFlow } = input;
  return {
    roadSurface: d.roadSurface,
    onRoadSurfaceChange: d.setRoadSurface,
    roadSmoothness: d.roadSmoothness,
    onRoadSmoothnessChange: d.setRoadSmoothness,
    roadCarriagewayCount: d.roadCarriagewayCount,
    onRoadCarriagewayCountChange: input.onRoadCarriagewayCountChange,
    roadLanes: d.roadLanes,
    onRoadLanesChange: d.setRoadLanes,
    roadLanesForward: d.roadLanesForward,
    onRoadLanesForwardChange: d.setRoadLanesForward,
    roadLanesBackward: d.roadLanesBackward,
    onRoadLanesBackwardChange: d.setRoadLanesBackward,
    roadLaneDirections: d.roadLaneDirections,
    onRoadLaneDirectionsChange: d.setRoadLaneDirections,
    roadManualLaneDirections: d.roadManualLaneDirections,
    onRoadManualLaneDirectionsChange: d.setRoadManualLaneDirections,
    roadUsePerLaneWidths: d.roadUsePerLaneWidths,
    onRoadUsePerLaneWidthsChange: d.setRoadUsePerLaneWidths,
    roadLaneWidthsM: d.roadLaneWidthsM,
    onRoadLaneWidthsMChange: d.setRoadLaneWidthsM,
    roadCarriagewayALaneWidthsM: d.roadCarriagewayALaneWidthsM,
    onRoadCarriagewayALaneWidthsMChange: d.setRoadCarriagewayALaneWidthsM,
    roadCarriagewayBLaneWidthsM: d.roadCarriagewayBLaneWidthsM,
    onRoadCarriagewayBLaneWidthsMChange: d.setRoadCarriagewayBLaneWidthsM,
    roadToll: d.roadToll,
    onRoadTollChange: d.setRoadToll,
    roadFloodProne: d.roadFloodProne,
    onRoadFloodProneChange: d.setRoadFloodProne,
    roadPotholes: d.roadPotholes,
    onRoadPotholesChange: d.setRoadPotholes,
    roadStreetVendors: d.roadStreetVendors,
    onRoadStreetVendorsChange: d.setRoadStreetVendors,
    roadAccidentProne: d.roadAccidentProne,
    onRoadAccidentProneChange: d.setRoadAccidentProne,
    roadHawkers: d.roadHawkers,
    onRoadHawkersChange: d.setRoadHawkers,
    roadLit: d.roadLit,
    onRoadLitChange: d.setRoadLit,
    roadDrainage: d.roadDrainage,
    onRoadDrainageChange: d.setRoadDrainage,
    roadMaxspeed: d.roadMaxspeed,
    onRoadMaxspeedChange: d.setRoadMaxspeed,
    roadCrossingType: d.roadCrossingType,
    onRoadCrossingTypeChange: d.setRoadCrossingType,
    roadWidth: d.roadWidth,
    onRoadWidthChange: d.setRoadWidth,
    roadCarriagewayAWidth: d.roadCarriagewayAWidth,
    onRoadCarriagewayAWidthChange: d.setRoadCarriagewayAWidth,
    roadCarriagewayBWidth: d.roadCarriagewayBWidth,
    onRoadCarriagewayBWidthChange: d.setRoadCarriagewayBWidth,
    singleStripCaptureMode: input.singleStripCaptureMode,
    onSingleStripCaptureModeChange: input.onSingleStripCaptureModeChange,
    stripACaptureMode: input.stripACaptureMode,
    onStripACaptureModeChange: input.onStripACaptureModeChange,
    stripBCaptureMode: input.stripBCaptureMode,
    onStripBCaptureModeChange: input.onStripBCaptureModeChange,
    divideWidthM: input.divideWidthM,
    onDivideWidthMChange: input.onDivideWidthMChange,
    dividedDivideReady: input.dividedDivideReady,
    dividedCarriagewayAReady: input.dividedCarriagewayAReady,
    dividedCarriagewayBReady: input.dividedCarriagewayBReady,
    dividedCarriagewayReady: input.dividedCarriagewayReady,
    stripATraceReady: input.stripATraceReady,
    stripBTraceReady: input.stripBTraceReady,
    singleStripTraceReady: input.singleStripTraceReady,
    onStartMarkingDivide: input.onStartMarkingDivide,
    onStartMarkingStripA: input.onStartMarkingStripA,
    onStartMarkingStripB: input.onStartMarkingStripB,
    onStartMarkingSingleStrip: input.onStartMarkingSingleStrip,
    dividedSurveySetupHint: input.dividedSurveySetupHint,
    onDismissDividedSurveySetupHint: input.onDismissDividedSurveySetupHint,
    roadGuidedSurveyFlow: motorRoadSurveyFlow,
    roadLayoutSubtitle:
      motorRoadSurveyFlow && input.selectedCategoryMeta
        ? `${input.selectedCategoryMeta.name} · ${roadLayoutLabel(d.roadCarriagewayCount)}`
        : '',
    roadGeometryStatusRows: motorRoadSurveyFlow ? input.roadGeometryStatusRows : [],
    onEditRoadGeometry: motorRoadSurveyFlow ? input.onEditRoadGeometry : undefined,
    roadCarriagewayALaneDirections: d.roadCarriagewayALaneDirections,
    onRoadCarriagewayALaneDirectionsChange: d.setRoadCarriagewayALaneDirections,
    roadCarriagewayBLaneDirections: d.roadCarriagewayBLaneDirections,
    onRoadCarriagewayBLaneDirectionsChange: d.setRoadCarriagewayBLaneDirections,
    roadSideFacilities: d.roadSideFacilities,
    onRoadSideFacilitiesChange: (patch) =>
      d.setRoadSideFacilities((prev) => ({ ...prev, ...patch })),
  };
}

export type { LaneDirection };
